import React from 'react';
import { ActionsDropdown, Card, CardBody, CardHeader, Badge } from '@aidkitorg/component-library';
import type { ComponentAuditResult } from '@aidkitorg/types/lib/admin-panel/distro-dashboard-migration';
import type * as survey from '@aidkitorg/types/lib/survey';
import { useLocalizedStrings } from '../../../Localization';

interface UnsupportedComponentPlaceholderProps {
  /** The original component that couldn't be adapted */
  component: survey.DashboardComponent;
  /** Audit information about why the component is unsupported */
  audit: ComponentAuditResult;
  /** Static widget ID from the dashboard widget library */
  staticWidgetId?: string;
  /** When true, shows delete action in card header */
  isEditMode?: boolean;
  /** Callback to delete this component from the dashboard */
  onDelete?: () => void;
}

/**
 * Placeholder card displayed for dashboard components that cannot be
 * rendered using admin panel components. Shows the component type,
 * title (if available), and reason for being unsupported.
 */
export const UnsupportedComponentPlaceholder: React.FC<UnsupportedComponentPlaceholderProps> = ({
  component,
  audit,
  staticWidgetId,
  isEditMode,
  onDelete,
}) => {
  const L = useLocalizedStrings();

  // Try to extract a title from the component
  const getComponentTitle = (): string => {
    if ('title' in component && component.title) {
      const title = component.title;
      if (typeof title === 'string') return title;
      if (typeof title === 'object' && 'en' in title) return title.en;
    }
    return audit.componentKind || 'Dashboard Component';
  };

  const title = getComponentTitle();

  const deleteAction = isEditMode && onDelete ? (
    <ActionsDropdown
      actions={[{
        label: L.admin_panel.dashboards.delete,
        onClick: onDelete,
        icon: 'TrashIcon',
        variant: 'warn' as const,
      }]}
      item={component}
    />
  ) : undefined;

  return (
    <Card className="rounded-md h-full">
      <CardHeader
        title={
          <div className="flex flex-wrap items-center gap-2 text-sm break-normal px-4 py-2">
            {title}
            {staticWidgetId && <Badge variant="neutral">{L.admin_panel.dashboards.static_widget}</Badge>}
          </div>
        }
        actionButton={deleteAction}
      />
      <CardBody className="flex-1 flex">
        <div className="flex-1 flex items-center justify-center text-center text-gray-500 text-sm px-4">
          {((component as any)._unresolved && staticWidgetId)
            ? L.admin_panel.dashboards.static_widget_unresolved
            : audit.message || L.admin_panel.dashboards.widget_will_render_on_save}
        </div>
      </CardBody>
    </Card>
  );
};
